import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  CheckCircle,
  Clock,
  Circle,
  Info,
  Upload,
  X,
  Image as ImageIcon,
  ChevronDown,
  ChevronRight,
  Search,
  Loader2,
  TerminalSquare,
  KeyboardIcon,
} from 'lucide-react';
import { MOCK_TEMPLATES } from './types';
import { useToast } from '@/hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { useQuery } from '@tanstack/react-query';
import { findAlertByReadableId } from '@/app/Alert/AlertAuditPage';
import { fetchAlert } from '@/lib/api/monitoring/alert';
import { MonitorAlertFullDto } from '@/app/Alert/types';
import { AlertPreviewDto } from '@/app/AlertsDashboard/types';

const templateSchema = z.object({
  prompt: z
    .string()
    .min(1, 'Prompt is required')
    .max(5000, 'Prompt must be less than 5000 characters'),
});

type TemplateFormData = z.infer<typeof templateSchema>;

interface ChatGPTResponse {
  d: string; // description
  c: 'intruder' | 'safety-event' | 'possibly-staff' | 'no-threat' | 'weather-event' | 'false-alarm'; // conclusion
  debug?: string; // additional analysis notes (only in debug mode)
}

interface TestResult {
  fileName: string;
  result?: ChatGPTResponse;
  error?: string;
  success: boolean;
  imageIndex: number;
}

interface PromptAnalysis {
  contradictions: string[];
  risks: string[];
  suggestions: string[];
  overallRisk: 'low' | 'medium' | 'high';
}

interface TestRun {
  id: string;
  prompt: string;
  timestamp: Date;
  results: TestResult[];
  promptAnalysis?: PromptAnalysis;
  promptAnalysisLoading?: boolean;
  promptAnalysisError?: string;
  summary: {
    total: number;
    threatConfirmed: number;
    threatNotConfirmed: number;
    indeterminate: number;
    errors: number;
  };
}

interface AlertFrame {
  id: string;
  imageUrl: string;
  timestamp: Date;
  type: string;
  eventId: string;
}


export function PromptTester() {
  const { templateId } = useParams<{ templateId: string }>();
  const { toast } = useToast();
  const isNew = templateId === 'new';
  const [uploadedImages, setUploadedImages] = useState<File[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [testRuns, setTestRuns] = useState<TestRun[]>([]);
  const [expandedRuns, setExpandedRuns] = useState<string[]>([]);
  const [isTestingPrompt, setIsTestingPrompt] = useState(false);
  const [currentTestingImage, setCurrentTestingImage] = useState<number | null>(null);
  
  // Alert search state
  const [alertIdInput, setAlertIdInput] = useState<string>('');
  const [searchInputAlertId, setSearchInputAlertId] = useState<string | null>(null);
  const [guidToFetch, setGuidToFetch] = useState<string | null>(null);
  const [alertSearchError, setAlertSearchError] = useState<string | null>(null);
  const [alertFrames, setAlertFrames] = useState<AlertFrame[]>([]);
  const [selectedAlertFrames, setSelectedAlertFrames] = useState<string[]>([]);
  const [currentTestImages, setCurrentTestImages] = useState<File[]>([]);
  const [promptMode, setPromptMode] = useState<'production' | 'debug'>('production');

  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useForm<TemplateFormData>({
    resolver: zodResolver(templateSchema),
    defaultValues: {
      prompt: '',
    },
  });

  useEffect(() => {
    if (!isNew && templateId) {
      const template = MOCK_TEMPLATES.find((t) => t.id === templateId);
      if (template) {
        setValue('prompt', template.prompt);
      }
    }
  }, [templateId, isNew, setValue]);


  // Image upload handlers
  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const validateFile = useCallback((file: File): boolean => {
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    if (!validTypes.includes(file.type)) {
      toast({
        title: 'Invalid file type',
        description: `${file.name} is not a valid image file. Please upload JPG, PNG, GIF, or WebP files.`,
        variant: 'destructive',
      });
      return false;
    }

    if (file.size > maxSize) {
      toast({
        title: 'File too large',
        description: `${file.name} is too large. Please upload files smaller than 10MB.`,
        variant: 'destructive',
      });
      return false;
    }

    return true;
  }, [toast]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const validFiles = files.filter(validateFile);
    
    setUploadedImages(prev => [...prev, ...validFiles]);
    
    if (validFiles.length > 0) {
      toast({
        title: 'Images uploaded',
        description: `${validFiles.length} image(s) uploaded successfully.`,
      });
    }
  }, [toast, validateFile]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    const validFiles = files.filter(validateFile);
    
    setUploadedImages(prev => [...prev, ...validFiles]);
    
    if (validFiles.length > 0) {
      toast({
        title: 'Images uploaded',
        description: `${validFiles.length} image(s) uploaded successfully.`,
      });
    }
    
    // Reset input
    e.target.value = '';
  }, [toast, validateFile]);

  // Create stable object URLs for uploaded images that persist across renders
  const uploadedImageUrls = useMemo(() => {
    return uploadedImages.map(file => URL.createObjectURL(file));
  }, [uploadedImages]);

  // Revoke object URLs when they change or component unmounts
  useEffect(() => {
    return () => {
      uploadedImageUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [uploadedImageUrls]);

  // Create stable object URLs for current test images
  const currentTestImageUrls = useMemo(() => {
    return currentTestImages.map(file => URL.createObjectURL(file));
  }, [currentTestImages]);

  // Revoke test image object URLs when they change or component unmounts
  useEffect(() => {
    return () => {
      currentTestImageUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [currentTestImageUrls]);

  const removeImage = useCallback((index: number) => {
    setUploadedImages(prev => prev.filter((_, i) => i !== index));
  }, []);



  const toggleTestRun = (runId: string) => {
    setExpandedRuns(prev => 
      prev.includes(runId)
        ? prev.filter(id => id !== runId)
        : [...prev, runId]
    );
  };

  const toggleAlertFrame = (frameId: string) => {
    setSelectedAlertFrames(prev => 
      prev.includes(frameId)
        ? prev.filter(id => id !== frameId)
        : [...prev, frameId]
    );
  };

  const handleSearchAlert = () => {
    if (alertIdInput.trim()) {
      setGuidToFetch(null);
      setAlertFrames([]);
      setSelectedAlertFrames([]);
      setAlertSearchError(null);
      setSearchInputAlertId(alertIdInput.trim());
    }
  };

  // Convert file to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data:image/jpeg;base64, prefix
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = error => reject(error);
    });
  };

  // Alert search queries
  const {
    data: foundAlertPreview,
    isLoading: isSearchingGuid,
    error: findGuidError,
    isFetching: isFetchingGuid,
  } = useQuery<AlertPreviewDto | null, Error>({
    queryKey: ['findAlertGuid', searchInputAlertId],
    queryFn: () => {
      if (!searchInputAlertId) return Promise.resolve(null);
      return findAlertByReadableId(searchInputAlertId);
    },
    enabled: !!searchInputAlertId,
    retry: 1,
  });
  
  const {
    data: alertData,
    isLoading: isAlertLoading,
    error: alertError,
    isFetching: isAlertFetching,
  } = useQuery<MonitorAlertFullDto, Error>({
    queryKey: ['alertAudit', guidToFetch],
    queryFn: () => {
      if (!guidToFetch) {
        return Promise.reject(new Error('Internal: GUID to fetch is missing'));
      }
      return fetchAlert(guidToFetch);
    },
    enabled: !!guidToFetch,
    retry: 1,
  });
  
  const isAlertSearchLoading = isSearchingGuid || isFetchingGuid || isAlertLoading || isAlertFetching;
  
  // Process alert data into frames
  useEffect(() => {
    setAlertSearchError(null);
    
    if (isAlertSearchLoading) {
      return;
    }
    
    if (findGuidError) {
      setAlertSearchError(`Error finding alert: ${findGuidError.message}`);
      setGuidToFetch(null);
    } else if (foundAlertPreview) {
      if (foundAlertPreview.id) {
        setGuidToFetch(foundAlertPreview.id);
      } else {
        setAlertSearchError(`Alert found by readable ID '${searchInputAlertId}', but it's missing a GUID.`);
        setGuidToFetch(null);
      }
    } else if (searchInputAlertId) {
      setAlertSearchError(`No alert found with ID: ${searchInputAlertId}`);
      setGuidToFetch(null);
    }
  }, [foundAlertPreview, isAlertSearchLoading, findGuidError, searchInputAlertId]);
  
  useEffect(() => {
    if (alertError) {
      setAlertSearchError(`Error loading alert: ${alertError.message}`);
      return;
    }
    
    if (alertData?.events) {
      const frames: AlertFrame[] = [];
      
      for (const event of alertData.events) {
        const imageValue = event.values?.find(v => v.type === 'SnapshotImage');
        if (imageValue?.sourceUrl) {
          frames.push({
            id: event.id,
            imageUrl: imageValue.sourceUrl,
            timestamp: new Date(event.occurred || Date.now()),
            type: event.type || 'Unknown',
            eventId: event.id
          });
        }
      }
      
      frames.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());
      setAlertFrames(frames);
    }
  }, [alertData, alertError]);
  
  // ChatGPT API integration
  const callChatGPTAPI = async (imageBase64: string, userPrompt: string) => {
    const productionPrompt = `
    @"We are a camera monitoring company who uses snapshot images from cameras to determine if there is or isn't a threat on site.


The order of the following instructions is important, only apply the instructions from the first condition met. I've used if else logic to clearly indicate logic.
Please determine if there is possibly a threat on this site, or no threat. Please provide a description of the threat. Hi-vis gear is not an indicator if someone works there.
If you think it's a threat/intruder, please describe in detail what the threat is, use the conclusion ""intruder"".



If you think it's staff on site, please use a 1-3 word description and use the conclusion ""possibly-staff""
If you can see blowing trees or rain dropping and no people or moving vehicles, please use a 1-3 word description and use the conclusion ""weather-event""
Otherwise, please use a 1-3 word description and use the conclusion ""false-alarm""

IMPORTANT - Description length rules:
- For ""intruder"": Provide a detailed description explaining the threat
- For all other conclusions: Use an extremely concise description (1-3 words max). Examples: ""Rain"", ""Wind"", ""Office workers"", ""Staff"", ""Cat"", ""Bird"", ""Delivery truck"", ""No activity""

Respond with JSON: {""d"": ""description"", ""c"": ""conclusion""}

Valid c values: intruder, safety-event, possibly-staff, no-threat, weather-event, false-alarm



These are the instructions for the site, only use them to influence your choice of which conclusion to choose. The site instructions can override the when you choose a certain conclusion.
<siteinstructions>
${userPrompt}
</siteinstructions>

  `;

    const debugPrompt = `
    @"We are a camera monitoring company who uses snapshot images from cameras to determine if there is or isn't a threat on site.


The order of the following instructions is important, only apply the instructions from the first condition met. I've used if else logic to clearly indicate logic.
Please determine if there is possibly a threat on this site, or no threat. Please provide a description of the threat. Hi-vis gear is not an indicator if someone works there.
If you think it's a threat/intruder, please describe in detail what the threat is, use the conclusion ""intruder"".



If you think it's staff on site, please use description explaining why and use the conclusion ""possibly-staff""
If you can see blowing trees or rain dropping and no people or moving vehicles, please use a description explaining why and use the conclusion ""weather-event""
Otherwise, please use a description explaining why and use the conclusion ""false-alarm""



IMPORTANT - Description length rules (DEBUG MODE):
- For ALL conclusions: Provide a detailed description explaining your reasoning
- Include specific observations from the image that led to your conclusion

Respond with JSON: {""d"": ""description"", ""c"": ""conclusion"", ""debug"": ""additional analysis notes""}

Valid c values: intruder, safety-event, possibly-staff, no-threat, weather-event, false-alarm



These are the instructions for the site, only use them to influence your choice of which conclusion to choose. The site instructions can override the when you choose a certain conclusion.
<siteinstructions>
${userPrompt}
</siteinstructions>

  `;

    const systemPrompt = promptMode === 'production' ? productionPrompt : debugPrompt;

    const proxyUrl = import.meta.env.VITE_PROXY_URL || 'https://lab-worker.watchful.co.nz';
    const response = await fetch(`${proxyUrl}/openai/v1/chat/completions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: 'gpt-4o',
        messages: [
          {
            role: 'system',
            content: systemPrompt
          },
          {
            role: 'user',
            content: [
              {
                type: 'text',
                text: `Threat Criteria: ${userPrompt}`
              },
              {
                type: 'image_url',
                image_url: {
                  url: `data:image/jpeg;base64,${imageBase64}`
                }
              }
            ]
          }
        ],
        max_tokens: 1000,
        temperature: 0.1
      })
    });

    if (!response.ok) {
      throw new Error(`API request failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    let content = data.choices[0].message.content;
    
    // Remove markdown code blocks if present
    content = content.replace(/```json\s*/, '').replace(/```\s*$/, '').trim();
    
    return JSON.parse(content);
  };

  // Analyze prompt for contradictions and risks
  const analyzePrompt = async (userPrompt: string): Promise<PromptAnalysis> => {
    const productionPrompt = `
    @"We are a camera monitoring company who uses snapshot images from cameras to determine if there is or isn't a threat on site.

The order of the following instructions is important, only apply the instructions from the first condition met. I've used if else logic to clearly indicate logic.
Please determine if there is possibly a threat on this site, or no threat. Please provide a description of the threat. Hi-vis gear is not an indicator if someone works there.
If you think it's a threat/intruder, please describe in detail what the threat is, use the conclusion ""intruder"".

If you think it's staff on site, please use a 1-3 word description and use the conclusion ""possibly-staff""
If you can see blowing trees or rain dropping and no people or moving vehicles, please use a 1-3 word description and use the conclusion ""weather-event""
Otherwise, please use a 1-3 word description and use the conclusion ""false-alarm""

Valid c values: intruder, safety-event, possibly-staff, no-threat, weather-event, false-alarm
    `;

    const analysisPrompt = `Check these site instructions for a security camera AI system.

SYSTEM PROMPT:
${productionPrompt}

SITE INSTRUCTIONS:
${userPrompt}

Find:
1. CONTRADICTIONS: Does it conflict when used together with the system prompt?
2. RISKS: Could it miss real threats or cause false alarms?
3. SUGGESTIONS: How to make it clearer?

Reply with JSON only:
{
  "contradictions": ["short plain English points"],
  "risks": ["short plain English points"],
  "suggestions": ["short plain English points"],
  "overallRisk": "low" | "medium" | "high"
}

Keep it simple. One sentence per point max.`;

    const proxyUrl = import.meta.env.VITE_PROXY_URL || 'https://lab-worker.watchful.co.nz';
    const response = await fetch(`${proxyUrl}/openai/v1/chat/completions`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        model: 'gpt-4o',
        messages: [
          {
            role: 'user',
            content: analysisPrompt
          }
        ],
        max_tokens: 1000,
        temperature: 0.3
      })
    });

    if (!response.ok) {
      throw new Error(`Prompt analysis failed: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    let content = data.choices[0].message.content;
    
    // Remove markdown code blocks if present
    content = content.replace(/```json\s*/, '').replace(/```\s*$/, '').trim();
    
    return JSON.parse(content);
  };

  // Convert alert frame URL to File object
  const urlToFile = async (url: string, filename: string): Promise<File> => {
    const response = await fetch(url);
    const blob = await response.blob();
    return new File([blob], filename, { type: blob.type });
  };

  // Test prompt handler
  const handleTestPrompt = async () => {
    

    // Get selected alert frames
    const selectedFrames = alertFrames.filter(frame => selectedAlertFrames.includes(frame.id));
    
    // Check if we have any images to test (uploaded or selected alert frames)
    if (uploadedImages.length === 0 && selectedFrames.length === 0) {
      toast({
        title: 'No Images',
        description: 'Please upload images or select alert frames to test the prompt.',
        variant: 'destructive',
      });
      return;
    }

    const userPrompt = watch('prompt');
    if (!userPrompt) {
      toast({
        title: 'No Prompt',
        description: 'Please enter a prompt to test.',
        variant: 'destructive',
      });
      return;
    }

    setIsTestingPrompt(true);
    
    // Combine uploaded images and selected alert frames
    let imagesToTest: File[] = [...uploadedImages];
    
    // Convert selected alert frames to File objects
    try {
      for (const frame of selectedFrames) {
        const file = await urlToFile(frame.imageUrl, `alert-${frame.id}-${frame.timestamp.getTime()}.jpg`);
        imagesToTest.push(file);
      }
    } catch {
      toast({
        title: 'Error Loading Alert Frames',
        description: 'Failed to load selected alert frames for testing.',
        variant: 'destructive',
      });
      setIsTestingPrompt(false);
      return;
    }
    
    const testRunId = `test-${Date.now()}`;
    const newTestRun: TestRun = {
      id: testRunId,
      prompt: userPrompt,
      timestamp: new Date(),
      results: [],
      promptAnalysisLoading: true,
      summary: {
        total: imagesToTest.length,
        threatConfirmed: 0,
        threatNotConfirmed: 0,
        indeterminate: 0,
        errors: 0
      }
    };
    
    setTestRuns(prev => [newTestRun, ...prev]);
    setExpandedRuns(prev => [testRunId, ...prev]);
    setCurrentTestImages(imagesToTest);

    // Fire off prompt analysis in parallel
    analyzePrompt(userPrompt)
      .then((analysis) => {
        setTestRuns(prev => prev.map(run => 
          run.id === testRunId 
            ? { ...run, promptAnalysis: analysis, promptAnalysisLoading: false }
            : run
        ));
      })
      .catch((error) => {
        setTestRuns(prev => prev.map(run => 
          run.id === testRunId 
            ? { ...run, promptAnalysisError: error instanceof Error ? error.message : 'Analysis failed', promptAnalysisLoading: false }
            : run
        ));
      });
    
    try {
      const results: TestResult[] = [];
      
      for (let i = 0; i < imagesToTest.length; i++) {
        setCurrentTestingImage(i);
        const file = imagesToTest[i];
        
        try {
          const base64 = await fileToBase64(file);
          const result = await callChatGPTAPI(base64, userPrompt);
          
          results.push({
            fileName: file.name,
            result,
            success: true,
            imageIndex: i
          });
        } catch (error) {
          results.push({
            fileName: file.name,
            error: error instanceof Error ? error.message : 'Unknown error occurred',
            success: false,
            imageIndex: i
          });
        }
        
        // Update test run incrementally
        const summary = {
          total: imagesToTest.length,
          threatConfirmed: results.filter(r => r.success && r.result?.c === 'intruder').length,
          threatNotConfirmed: results.filter(r => r.success && (r.result?.c === 'no-threat' || r.result?.c === 'possibly-staff')).length,
          indeterminate: results.filter(r => r.success && ['safety-event', 'weather-event', 'false-alarm'].includes(r.result?.c || '')).length,
          errors: results.filter(r => !r.success).length
        };
        
        setTestRuns(prev => prev.map(run => 
          run.id === testRunId 
            ? { ...run, results: [...results], summary }
            : run
        ));
      }
      
      toast({
        title: 'Testing Complete',
        description: `Processed ${results.length} images successfully.`,
      });
    } catch (error) {
      toast({
        title: 'Testing Failed',
        description: error instanceof Error ? error.message : 'Unknown error occurred',
        variant: 'destructive',
      });
    } finally {
      setIsTestingPrompt(false);
      setCurrentTestingImage(null);
    }
  };

  return (
    <div className="">
   

      <div className="grid grid-cols-[400px_1fr_400px]">
        <div className="border-r ">
            <div className='sticky top-14'>
                   <h2 className="border-b p-4 text-lg font-semibold flex items-center gap-2"><KeyboardIcon/>Prompt Input</h2>
          <div className="space-y-4 p-4">
           


       

           

            {/* Prompt Mode Selection */}
            <div className="space-y-2">
              <Label className="text-gray-200">Prompt Mode</Label>
              <div className="flex gap-2 flex-col">
                <label className="flex items-center gap-2 cursor-pointer border p-4 rounded-lg">
                  <input
                    type="radio"
                    name="promptMode"
                    value="production"
                    checked={promptMode === 'production'}
                    onChange={() => setPromptMode('production')}
                    className="accent-lime"
                  />
                 <div>
                  <p className="text-sm text-gray-300 font-semibold">Production</p><p className="text-xs text-gray-400">Use the same prompt and model as in production.</p></div>
                </label>
                <label className="flex items-center gap-2 cursor-pointer border p-4 rounded-lg">
                  <input
                    type="radio"
                    name="promptMode"
                    value="debug"
                    checked={promptMode === 'debug'}
                    onChange={() => setPromptMode('debug')}
                    className="accent-lime"
                  />
                  <div>
                  <p className="text-sm text-gray-300 font-semibold">Debug</p><p className="text-xs text-gray-400">Use the debug prompt and model to test the prompt.</p></div>
                </label>
              </div>
            </div>

            {/* Prompt */}
            <div className="space-y-2">
              <Label htmlFor="prompt" className="text-gray-200">
Prompt
              </Label>
              <Textarea
                id="prompt"
                {...register('prompt')}
                placeholder="Enter the prompt template. Use {variable_name} for dynamic values."
                rows={24}
                className="font-mono text-sm text-white placeholder:text-gray-400"
              />
          
              {errors.prompt && <p className="text-sm text-red-400">{errors.prompt.message}</p>}
            </div>

          </div>

      
          </div>
        </div>
        <div>
          <Tabs defaultValue="upload">
            <TabsList className="w-full h-[61px] border-x-0 border-t-0 rounded-none">
              <TabsTrigger value="upload">Upload Image</TabsTrigger>
              {/* <TabsTrigger value="sample">Sample Images</TabsTrigger> */}
              <TabsTrigger value="alerts">Recent Alerts</TabsTrigger>
            </TabsList>
            <TabsContent value="upload" className="">
                  <div className='p-4'>
                 <h4 className="text-lg font-semibold text-white">Upload Images</h4>
                  <p className="text-xs text-gray-400">
                    Upload images to test your prompts
                  </p></div>
                  
              <div className="">
               

                {/* Drag and Drop Area */}
                <div
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                  className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors m-4 mt-0 ${
                    isDragOver
                      ? 'border-lime bg-lime/5'
                      : ' hover:border-gray-500'
                  }`}
                >
                  <input
                    type="file"
                    multiple
                    accept="image/*"
                    onChange={handleFileSelect}
                    className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                  />
                  <div className="flex flex-col items-center gap-4">
                    <div className={`p-4 rounded-full ${
                      isDragOver ? 'bg-lime/20' : 'bg-gray-700'
                    }`}>
                      <Upload className={`h-8 w-8 ${
                        isDragOver ? 'text-lime' : 'text-gray-400'
                      }`} />
                    </div>
                    <div>
                      <p className="text-lg font-medium text-white mb-1">
                        {isDragOver ? 'Drop images here' : 'Drag & drop images here'}
                      </p>
                      <p className="text-sm text-gray-400">
                        or <span className="text-lime font-medium">browse files</span>
                      </p>
                    </div>
                  </div>
                </div>

                {/* Uploaded Images Preview */}
                {uploadedImages.length > 0 && (
                  <div className="space-y-3 ">
                    <div className="flex items-center justify-between border-y p-4">
                      <h4 className="text-sm font-semibold text-white">
                        Uploaded Images ({uploadedImages.length})
                      </h4>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setUploadedImages([]);
                          setTestRuns([]);
                        }}
                        className="text-xs"
                      >
                        Clear All
                      </Button>
                    </div>
                    <div className="grid grid-cols-5 gap-3  overflow-y-auto m-4">
                      {uploadedImages.map((file, index) => (
                        <div key={index} className="relative group">
                          <div className="aspect-square rounded-lg overflow-hidden bg-gray-800 border ">
                            <img
                              src={uploadedImageUrls[index]}
                              alt={file.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <button
                            type="button"
                            onClick={() => removeImage(index)}
                            className="absolute top-2 right-2 p-1 bg-lime text-black rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-lime/80"
                          >
                            <X className="h-3 w-3" />
                          </button>
                          <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-2 rounded-b-lg">
                            <p className="truncate font-semibold" title={file.name}>{file.name}</p>
                            <p className="text-gray-400">{(file.size / 1024 / 1024).toFixed(1)} MB</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Upload Stats */}
                <div className="flex items-center gap-4 text-xs text-gray-500 pt-2 p-4">
                  <div className="flex items-center gap-1">
                    <ImageIcon className="h-3 w-3" />
                    <span>Max 10MB per image</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Upload className="h-3 w-3" />
                    <span>JPG, PNG, GIF, WebP supported</span>
                  </div>
                </div>
              </div>
            </TabsContent>
          
            
            <TabsContent value="alerts">


              <div className="">
                <div className='p-4 pb-2'>
                 <h4 className="text-lg font-semibold text-white">Alert Search</h4>
                  <p className="text-xs text-gray-400">
                    Search for an alert by ID to load and select frames for testing
                  </p></div>
                  
                <div className="">
                 
                  <div className="flex gap-2 p-4 pt-0">
                    <Input
                    isSearch
                      value={alertIdInput}
                      onChange={(e) => setAlertIdInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' && !isAlertSearchLoading && alertIdInput.trim()) {
                          handleSearchAlert();
                        }
                      }}
                      placeholder="Enter Alert ID (e.g., A-123)"
                      className="flex-1"
                    />
                    <Button
                      onClick={handleSearchAlert}
                      disabled={isAlertSearchLoading || !alertIdInput.trim()}
                      size="sm"
                    >
                      {isAlertSearchLoading ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <><Search className="h-4 w-4" />Search</>
                      )}
                    </Button>
                  </div>
                  
                  {alertSearchError && (
                    <div className="text-xs text-red-400 bg-red-900/20 border border-red-800 rounded p-2 mx-4">
                      {alertSearchError}
                    </div>
                  )}
                </div>

                {/* Alert Frames */}
                {alertFrames.length > 0 && (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-4 border-y">
                      <h4 className="text-lg font-semibold text-white">
                        Results for  {alertIdInput ? `${alertIdInput} ` : ''} ({alertFrames.length})
                      </h4>
                      <div className="flex gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const allSelected = alertFrames.every(frame => selectedAlertFrames.includes(frame.id));
                            if (allSelected) {
                              setSelectedAlertFrames([]);
                            } else {
                              setSelectedAlertFrames(alertFrames.map(frame => frame.id));
                            }
                          }}
                          className="text-xs"
                        >
                          {alertFrames.every(frame => selectedAlertFrames.includes(frame.id)) ? 'Deselect All' : 'Select All'}
                        </Button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-3 overflow-y-auto m-6">
                      {alertFrames.map((frame) => (
                        <div
                          key={frame.id}
                          className={`relative group cursor-pointer border-2 rounded-lg overflow-hidden transition-colors ${
                            selectedAlertFrames.includes(frame.id)
                              ? 'border-lime bg-lime/10'
                              : ' hover:border-gray-500'
                          }`}
                          onClick={() => toggleAlertFrame(frame.id)}
                        >
                          <div className="aspect-square bg-gray-800">
                            <img
                              src={frame.imageUrl}
                              alt={`Alert frame ${frame.timestamp.toLocaleTimeString()}`}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          
                          {selectedAlertFrames.includes(frame.id) && (
                            <div className="absolute top-2 right-2 w-5 h-5 bg-lime rounded-full flex items-center justify-center">
                              <CheckCircle className="h-3 w-3 text-black" />
                            </div>
                          )}
                          
                          <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs p-2">
                            <p className="truncate">{frame.timestamp.toLocaleTimeString()}</p>
                            <p className="text-gray-300">{frame.type}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {selectedAlertFrames.length > 0 && (
                      <div className="text-xs text-gray-400">
                        {selectedAlertFrames.length} frame{selectedAlertFrames.length !== 1 ? 's' : ''} selected
                      </div>
                    )}
                  </div>
                )}
              </div>
            </TabsContent>
          </Tabs>
        </div>

        {/* Actions */}
        <div className="border-l ">
          <div className='sticky top-14 h-[calc(100vh-56px)] overflow-y-scroll'>
            <h2 className="border-b p-4 text-lg font-semibold flex items-center gap-2 sticky top-0 bg-[#1b1b1b]"><TerminalSquare/>Output</h2>
            <div className="border-b p-4">
              <Button 
                className="w-full" 
                onClick={handleTestPrompt}
                disabled={isTestingPrompt || !watch('prompt') || (uploadedImages.length === 0 && selectedAlertFrames.length === 0)}
              >
                {isTestingPrompt ? (
                  <>
                    <Clock className="mr-2 h-4 w-4 animate-spin" />
                    Testing {currentTestingImage !== null ? `${currentTestingImage + 1}/${uploadedImages.length + selectedAlertFrames.length}` : '...'}
                  </>
                ) : (
                  `Test Prompt (${uploadedImages.length + selectedAlertFrames.length} images)`
                )}
              </Button>
            </div>
            {/* Test Results */}
            <div className="flex-1 overflow-y-auto">
              {testRuns.length === 0 && !isTestingPrompt ? (
                <div className="p-4 text-center text-gray-500">
                  <p>No test results yet.</p>
                  <p className="text-xs mt-1">Upload images and click "Test Prompt" to see results.</p>
                </div>
              ) : (
                <div className="space-y-3 p-4">
                  {testRuns.map((testRun) => {
                    const isExpanded = expandedRuns.includes(testRun.id);
                    return (
                      <Collapsible key={testRun.id} defaultOpen={true} open={isExpanded} onOpenChange={() => toggleTestRun(testRun.id)}>
                        <div className="border  rounded-lg">
                          <CollapsibleTrigger className="w-full p-4 text-left hover:bg-gray-800/50 transition-colors">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-2">
                               
                                <div>
                                  <div className="font-semibold text-white text-sm">
                                    Test Run
                                  </div>
                                  <div className="text-xs text-gray-400 mt-1 line-clamp-2">
                               {testRun.timestamp.toLocaleTimeString()}
                                  </div>
                                </div>
                              </div>
                              <div className="flex items-center gap-2 text-xs">
                              {isExpanded ? (
                                  <ChevronDown className="h-4 w-4 text-lime" />
                                ) : (
                                  <ChevronRight className="h-4 w-4 text-lime" />
                                )}
                              </div> 
                            </div>
                          </CollapsibleTrigger>
                          
                          <CollapsibleContent>
                            <div className="border-t  ">
                              {/* Test Run Details */}
                              <div className="space-y-2 p-4 border-b">
                                <div className="text-xs text-gray-400">
                                  <strong>Tested Prompt:</strong>
                                  <div className="mt-1 p-2 bg-gray-800/50 rounded text-gray-300 font-mono text-xs">
                                    {testRun.prompt}
                                  </div>
                                </div>
                                
                                <div className="grid grid-cols-3 gap-2 text-xs">
                                  <div className="text-start p-2 bg-gray-600/10 rounded col-span-2">
                                    <div className="text-gray-400 ">Summary</div>
                                    <div className="text-white font-semibold text-xl">  {isTestingPrompt && currentTestingImage !== null ? (
                      <>Processing...</>
                    ) : (
                      `${testRun.summary.total} processed `
                    )}</div>
                                  </div>
                           <div className="text-start p-2 bg-gray-600/10 rounded">
                                    <div className="text-white">Positive Rate</div>
                                    <div className="text-white font-semibold text-xl">{(testRun.summary.threatConfirmed / testRun.summary.total * 100).toFixed(1)}%</div>
                                  </div>
                       
                                       
                                  <div className="text-start p-2 bg-gray-600/10 rounded">
                                    <div className="text-white">Threats</div>
                                    <div className="text-white font-semibold text-xl">{testRun.summary.threatConfirmed}</div>
                                  </div>
                                    <div className="text-start p-2 bg-gray-600/10 rounded">
Indeterminate                                    <div className="text-white font-semibold text-xl">{testRun.summary.indeterminate}</div>
                                  </div>


                                  <div className="text-start p-2 bg-gray-600/10 rounded">
                                    <div className="text-white">Non Threats</div>
                                    <div className="text-white font-semibold text-xl">{testRun.summary.threatNotConfirmed}</div>
                                  </div>   
                                          

                                </div>
                              </div>
                              
                              {/* Prompt Analysis */}
                              <Collapsible defaultOpen={false}>
                                <div className="border-b">
                                  <CollapsibleTrigger className="w-full">
                                    <div className="flex items-center justify-between p-3 cursor-pointer hover:bg-gray-800/50">
                                      <div className="flex items-center gap-3">
                                        <ChevronRight className="h-4 w-4 text-lime transition-transform [[data-state=open]_&]:rotate-90" />
                                        <h4 className="font-semibold text-white text-sm">Prompt Analysis</h4>
                                      </div>
                                      {testRun.promptAnalysisLoading ? (
                                        <div className="flex items-center gap-1 rounded-full border border-gray-600 bg-gray-600/20 px-2 py-1 text-xs font-semibold text-gray-300">
                                          <Loader2 size={12} className="animate-spin" />
                                          Analyzing...
                                        </div>
                                      ) : testRun.promptAnalysisError ? (
                                        <div className="flex items-center gap-1 rounded-full border border-red-600 bg-red-600/20 px-2 py-1 text-xs font-semibold text-red-300">
                                          <X size={12} />
                                          Error
                                        </div>
                                      ) : testRun.promptAnalysis ? (
                                        <div className={`flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-semibold ${
                                          testRun.promptAnalysis.overallRisk === 'high'
                                            ? 'bg-red-600/20 text-red-300 border-red-600'
                                            : testRun.promptAnalysis.overallRisk === 'medium'
                                            ? 'bg-yellow-600/20 text-yellow-300 border-yellow-600'
                                            : 'bg-green-600/20 text-green-300 border-green-600'
                                        }`}>
                                          {testRun.promptAnalysis.overallRisk === 'high' ? (
                                            <X size={12} />
                                          ) : testRun.promptAnalysis.overallRisk === 'medium' ? (
                                            <Info size={12} />
                                          ) : (
                                            <CheckCircle size={12} />
                                          )}
                                          {testRun.promptAnalysis.overallRisk} risk
                                        </div>
                                      ) : null}
                                    </div>
                                  </CollapsibleTrigger>
                                  <CollapsibleContent>
                                    <div className="p-3  space-y-3 border-t">
                                      {testRun.promptAnalysisLoading && (
                                        <div className="text-sm text-gray-400 flex items-center gap-2">
                                          <Loader2 size={14} className="animate-spin" />
                                          Analyzing prompt for contradictions and risks...
                                        </div>
                                      )}
                                      {testRun.promptAnalysisError && (
                                        <div className="text-sm text-red-400">
                                          {testRun.promptAnalysisError}
                                        </div>
                                      )}
                                      {testRun.promptAnalysis && (
                                        <>
                                          {testRun.promptAnalysis.contradictions.length > 0 && (
                                            <div>
                                              <h5 className="text-xs font-semibold text-red-400 mb-1">Contradictions</h5>
                                              <ul className="text-xs text-gray-300 space-y-1">
                                                {testRun.promptAnalysis.contradictions.map((item, i) => (
                                                  <li key={i} className="flex items-start gap-2">
                                                    <span className="text-red-400 mt-0.5">•</span>
                                                    {item}
                                                  </li>
                                                ))}
                                              </ul>
                                            </div>
                                          )}
                                          {testRun.promptAnalysis.risks.length > 0 && (
                                            <div>
                                              <h5 className="text-xs font-semibold text-yellow-400 mb-1">Risks</h5>
                                              <ul className="text-xs text-gray-300 space-y-1">
                                                {testRun.promptAnalysis.risks.map((item, i) => (
                                                  <li key={i} className="flex items-start gap-2">
                                                    <span className="text-yellow-400 mt-0.5">•</span>
                                                    {item}
                                                  </li>
                                                ))}
                                              </ul>
                                            </div>
                                          )}
                                          {testRun.promptAnalysis.suggestions.length > 0 && (
                                            <div>
                                              <h5 className="text-xs font-semibold text-blue-400 mb-1">Suggestions</h5>
                                              <ul className="text-xs text-gray-300 space-y-1">
                                                {testRun.promptAnalysis.suggestions.map((item, i) => (
                                                  <li key={i} className="flex items-start gap-2">
                                                    <span className="text-blue-400 mt-0.5">•</span>
                                                    {item}
                                                  </li>
                                                ))}
                                              </ul>
                                            </div>
                                          )}
                                          {testRun.promptAnalysis.contradictions.length === 0 && 
                                           testRun.promptAnalysis.risks.length === 0 && 
                                           testRun.promptAnalysis.suggestions.length === 0 && (
                                            <div className="text-xs text-green-400">
                                              No issues found. Prompt looks good!
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  </CollapsibleContent>
                                </div>
                              </Collapsible>

                              {/* Individual Results */}
                              <div className="">
                                {testRun.results.map((result, index) => (
                                  <Collapsible key={index} defaultOpen={false}>
                                    <div className="border-b">
                                      <CollapsibleTrigger className="w-full">
                                        <div className="flex items-center justify-between p-2 cursor-pointer hover:bg-gray-800/50 rounded-t-lg">
                                          <div className="flex items-center gap-3">
                                            <ChevronRight className="h-4 w-4 text-lime transition-transform [[data-state=open]_&]:rotate-90" />
                                            <h4 className="font-semibold text-white text-sm truncate max-w-48" title={result.fileName}>
                                            Image {index + 1}
                                            </h4>
                                          </div>
                                          {result.success ? (
                                            <div className={`flex items-center gap-1 rounded-full border px-2 py-1 text-xs font-semibold ${
                                              result.result?.c === 'intruder'
                                                ? 'bg-red-600/20 text-red-300 border-red-600'
                                                : result.result?.c === 'no-threat' || result.result?.c === 'possibly-staff'
                                                ? 'bg-green-600/20 text-green-300 border-green-600'
                                                : 'bg-yellow-600/20 text-yellow-300 border-yellow-600'
                                            }`}>
                                              {result.result?.c === 'intruder' ? (
                                                <CheckCircle size={12} />
                                              ) : result.result?.c === 'no-threat' || result.result?.c === 'possibly-staff' ? (
                                                <Circle size={12} />
                                              ) : (
                                                <Info size={12} />
                                              )}
                                              {result.result?.c.replace('-', ' ')}
                                            </div>
                                          ) : (
                                            <div className="flex items-center gap-1 rounded-full border border-red-600 bg-red-600/20 px-2 py-1 text-xs font-semibold text-red-300">
                                              <X size={12} />
                                              Error
                                            </div>
                                          )}
                                        </div>
                                      </CollapsibleTrigger>
                                      <CollapsibleContent>
                                        {currentTestImageUrls[result.imageIndex] ? (
                                          <img 
                                            src={currentTestImageUrls[result.imageIndex]} 
                                            alt={result.fileName}
                                            className="w-full object-cover rounded border "
                                          />
                                        ) : (
                                          <div className="w-12 h-12 bg-gray-700 rounded border  flex items-center justify-center">
                                            <ImageIcon className="h-6 w-6 text-gray-500" />
                                          </div>
                                        )}
                                        
                                        {result.success && result.result ? (
                                          <div className="space-y-2 p-2 pt-2">
                                            <div className="text-xs text-gray-400">
                                              <strong>Description:</strong>
                                              <p className="mt-1 text-gray-300">{result.result.d}</p>
                                            </div>
                                            
                                            <div className="text-xs text-gray-400">
                                              <strong>Conclusion:</strong>
                                              <p className="mt-1 text-gray-300 capitalize">{result.result.c.replace('-', ' ')}</p>
                                            </div>
                                            {promptMode === 'debug' && result.result.debug && (
                                              <div className="text-xs text-gray-400">
                                                <strong>Debug:</strong>
                                                <p className="mt-1 text-gray-300">{result.result.debug}</p>
                                              </div>
                                            )}
                                          </div>
                                        ) : (
                                          <div className="text-xs text-red-400 p-2">
                                            <strong>Error:</strong> {result.error}
                                          </div>
                                        )}
                                      </CollapsibleContent>
                                    </div>
                                  </Collapsible>
                                ))}
                              </div>
                            </div>
                          </CollapsibleContent>
                        </div>
                      </Collapsible>
                    );
                  })}
                  
                
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PromptTester;
